import { useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import type { Content, GameState, Problem } from '@neopolis/engine';

import { T } from '../../textes-interface.js';

/**
 * Les places du podium, la première étant la priorité numéro un.
 *
 * Leur nombre vient de `reglages.places_au_podium` : il ne peut donc plus être
 * calculé une fois pour toutes au chargement du module.
 */
const placesDuPodium = (content: Content): readonly number[] =>
  Array.from({ length: content.reglages.places_au_podium }, (_, index) => index);

function ProblemeDisponible({
  probleme,
  selectionne,
  onSelectionner,
}: {
  probleme: Problem;
  selectionne: boolean;
  onSelectionner: () => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: probleme.id });

  return (
    <button
      {...attributes}
      {...listeners}
      ref={setNodeRef}
      type="button"
      aria-pressed={selectionne}
      onClick={onSelectionner}
      className={`min-h-touch w-full cursor-grab rounded-xl border-2 px-3 py-2 text-left font-medium ${
        selectionne ? 'border-sky-600 bg-sky-50' : 'border-slate-300 bg-white'
      } ${isDragging ? 'opacity-40' : ''}`}
    >
      {probleme.titre}
    </button>
  );
}

function Place({
  index,
  probleme,
  selection,
  onDeposer,
  onRetirer,
}: {
  index: number;
  probleme: Problem | null;
  selection: string | null;
  onDeposer: () => void;
  onRetirer: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `place-${index}` });

  return (
    <li
      ref={setNodeRef}
      className={`rounded-2xl border-2 p-3 transition ${
        isOver ? 'border-sky-600 bg-sky-50' : 'border-slate-200 bg-white'
      }`}
    >
      <p className="text-sm font-bold uppercase tracking-wide text-slate-500">
        {T.decider.place} {index + 1}
      </p>

      {probleme ? (
        <>
          <p className="mt-1 font-semibold text-slate-900">{probleme.titre}</p>
          <button
            type="button"
            onClick={onRetirer}
            aria-label={`${T.decider.retirer} : ${probleme.titre}`}
            className="mt-2 min-h-touch w-full rounded-xl bg-slate-100 px-3 text-sm font-semibold text-slate-700"
          >
            {T.decider.retirer}
          </button>
        </>
      ) : (
        <button
          type="button"
          disabled={selection === null}
          onClick={onDeposer}
          aria-label={`${T.decider.ajouter} — ${T.decider.place} ${index + 1}`}
          className="mt-1 min-h-touch w-full rounded-xl border-2 border-dashed border-slate-300 px-3 text-sm font-semibold text-slate-600 disabled:opacity-40"
        >
          {T.decider.vide}
        </button>
      )}
    </li>
  );
}

/**
 * Étape 4 — Décider.
 *
 * L'îlot met trois problèmes sur un podium, puis justifie son choix au maire.
 * Comme au classement, tout se fait au glisser-déposer **ou** au clic et au
 * clavier.
 */
export function EtapeDecider({
  content,
  partie,
  onDefinirPriorites,
  onJustifier,
}: {
  content: Content;
  partie: GameState;
  onDefinirPriorites: (priorites: readonly string[]) => void;
  onJustifier: (texte: string) => void;
}) {
  const [selection, setSelection] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const parId = new Map(content.problemes.map((p) => [p.id, p]));
  const decouverts = partie.discoveredProblems
    .map((id) => parId.get(id))
    .filter((p): p is Problem => p !== undefined);

  const disponibles = decouverts.filter((p) => !partie.priorities.includes(p.id));

  const poser = (problemId: string, place: number): void => {
    const suivantes = [...partie.priorities];
    const dejaLa = suivantes.indexOf(problemId);
    if (dejaLa !== -1) {
      suivantes.splice(dejaLa, 1);
    }
    while (suivantes.length < place) {
      suivantes.push('');
    }
    suivantes[place] = problemId;
    onDefinirPriorites(suivantes.filter((id) => id !== ''));
    setSelection(null);
  };

  const retirer = (problemId: string): void => {
    onDefinirPriorites(partie.priorities.filter((id) => id !== problemId));
  };

  const surFinDeGlisser = (evenement: DragEndEvent): void => {
    const cible = evenement.over?.id;
    if (
      typeof cible === 'string' &&
      cible.startsWith('place-') &&
      typeof evenement.active.id === 'string'
    ) {
      poser(evenement.active.id, Number(cible.slice('place-'.length)));
    }
  };

  const champJustification = content.chapitres
    .find((c) => c.id === 'ch1')
    ?.trace_ecrite.champs.find((f) => f.id === 'justification');

  return (
    <DndContext sensors={sensors} onDragEnd={surFinDeGlisser}>
      <div className="space-y-4">
        <p className="text-slate-700">{T.decider.consigne}</p>

        <section aria-label={T.decider.podium}>
          <ol className="grid gap-3 sm:grid-cols-3">
            {placesDuPodium(content).map((index) => (
              <Place
                key={index}
                index={index}
                probleme={parId.get(partie.priorities[index] ?? '') ?? null}
                selection={selection}
                onDeposer={() => selection && poser(selection, index)}
                onRetirer={() => {
                  const occupant = partie.priorities[index];
                  if (occupant) retirer(occupant);
                }}
              />
            ))}
          </ol>
        </section>

        <section className="rounded-2xl border-2 border-slate-200 bg-white p-3">
          <h3 className="font-bold text-slate-900">{T.classer.aRanger}</h3>
          <ul className="mt-2 grid gap-2 sm:grid-cols-2">
            {disponibles.map((probleme) => (
              <li key={probleme.id}>
                <ProblemeDisponible
                  probleme={probleme}
                  selectionne={selection === probleme.id}
                  onSelectionner={() =>
                    setSelection((precedent) => (precedent === probleme.id ? null : probleme.id))
                  }
                />
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-2xl border-2 border-slate-200 bg-white p-3">
          <label htmlFor="justification" className="block font-bold text-slate-900">
            {champJustification?.label ?? T.decider.justification}
          </label>
          <p className="mt-1 text-sm text-slate-600">{T.decider.justificationAide}</p>
          <textarea
            id="justification"
            rows={4}
            value={partie.writtenTrace.ch1['justification'] ?? ''}
            onChange={(evenement) => onJustifier(evenement.target.value)}
            className="mt-2 w-full rounded-xl border-2 border-slate-300 p-3 focus:border-sky-600"
          />
        </section>
      </div>
    </DndContext>
  );
}
